import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Crown, X, CheckCircle2, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UpgradeModal({
  show,
  onClose,
  currentTier,
  upgradingTo,
  handleUpgrade
}) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-xl font-bold">Upgrade Your Plan</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Standard Plan */}
          <div className={`border rounded-xl p-5 ${currentTier === 'standard' ? 'border-sky-500 bg-sky-500/5' : 'border-slate-700'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-sky-400" />
              <span className="font-bold">Standard</span>
              {currentTier === 'standard' && (
                <span className="text-xs bg-sky-500/20 text-sky-400 px-2 py-0.5 rounded-full">Current</span>
              )}
            </div>
            <div className="text-2xl font-bold mb-3">$47.98<span className="text-sm text-slate-400 font-normal">/mo</span></div>
            <ul className="space-y-2 mb-4 text-sm text-slate-300">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400" /> Lead management</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400" /> Built-in calendar</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400" /> Basic analytics</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-sky-400" /> Email support</li>
            </ul>
            {currentTier !== 'standard' && currentTier !== 'premium' && (
              <Button
                onClick={() => handleUpgrade('standard')}
                disabled={upgradingTo === 'standard'}
                className="w-full bg-sky-500 hover:bg-sky-600"
                data-testid="upgrade-standard-btn"
              >
                {upgradingTo === 'standard' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upgrade to Standard'}
              </Button>
            )}
          </div>
          
          {/* Premium Plan */}
          <div className={`border rounded-xl p-5 relative ${currentTier === 'premium' ? 'border-yellow-500 bg-yellow-500/5' : 'border-yellow-500/50'}`}>
            {currentTier !== 'premium' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full text-xs font-semibold text-black">
                Recommended
              </div>
            )}
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span className="font-bold">Premium</span>
              {currentTier === 'premium' && (
                <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">Current</span>
              )}
            </div>
            <div className="text-2xl font-bold mb-3">$397<span className="text-sm text-slate-400 font-normal">/mo</span></div>
            <ul className="space-y-2 mb-4 text-sm text-slate-300">
              <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Everything in Standard</li>
              <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Google Calendar sync</li>
              <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Review management</li>
              <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> AI-powered analytics</li>
              <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Priority support</li>
            </ul>
            {currentTier !== 'premium' && (
              <Button
                onClick={() => handleUpgrade('premium')}
                disabled={upgradingTo === 'premium'}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400"
                data-testid="upgrade-premium-btn"
              >
                {upgradingTo === 'premium' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upgrade to Premium'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
